import { useEffect, useState } from 'react';
import type { SupabaseClient } from '../supabase/client';
import type { AuthSession } from '../types/auth';

/**
 * Subscribe to the current Supabase auth session. Takes the client as a
 * parameter so packages/shared does not depend on any app's lib/ folder.
 *
 * Server-side validation: `supabase.auth.getSession()` is local-only — it
 * decodes the cached JWT from storage without asking the server whether the
 * user still exists. If the database was reset (or the user was deleted), the
 * client still sees a "session" and happy-paths through AuthGuard / publish
 * flows until an RLS-protected query fails. To avoid silent stale-session
 * behavior, when getSession() returns a session we also call getUser(), which
 * round-trips to the auth server. A real auth error from the server (user
 * deleted, JWT rejected) triggers signOut(); a retryable network error
 * (offline) is treated as "trust the cache" so a wifi blip does not sign the
 * user out.
 */
export function useAuthSession(client: SupabaseClient): AuthSession {
  const [state, setState] = useState<AuthSession>({
    session: null,
    user: null,
    isLoading: true,
    isEmailVerified: false,
  });

  useEffect(() => {
    let cancelled = false;
    let hasHydrated = false;

    async function hydrate() {
      const { data } = await client.auth.getSession();
      if (cancelled) return;

      if (!data.session) {
        hasHydrated = true;
        setState({ session: null, user: null, isLoading: false, isEmailVerified: false });
        return;
      }

      const { error } = await client.auth.getUser();
      if (cancelled) return;

      if (error && error.name !== 'AuthRetryableFetchError') {
        await client.auth.signOut();
        if (cancelled) return;
        hasHydrated = true;
        setState({ session: null, user: null, isLoading: false, isEmailVerified: false });
        return;
      }

      hasHydrated = true;
      setState({
        session: data.session,
        user: data.session.user,
        isLoading: false,
        isEmailVerified: !!data.session.user.email_confirmed_at,
      });
    }

    void hydrate();

    const { data: sub } = client.auth.onAuthStateChange((_event, session) => {
      // Supabase can emit a null auth event before our explicit getSession()
      // round-trip has finished. Route guards should keep their loading state
      // until hydrate settles, otherwise a transient null session can redirect
      // authenticated shoppers back to signin.
      if (!hasHydrated && !session) return;

      setState({
        session,
        user: session?.user ?? null,
        isLoading: false,
        isEmailVerified: !!session?.user?.email_confirmed_at,
      });
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [client]);

  return state;
}
